import { useEffect } from 'react';
import get from 'platform/utilities/data/get';
import { isDeepEmpty, withUrlPrefix } from './helpers';

function filterEmptyItems(arrayData) {
  return arrayData?.length
    ? arrayData.filter(item => !isDeepEmpty(item))
    : arrayData;
}

/**
 * Runs one-time array builder summary page setup on mount. Behavior-preserving
 * extraction from ArrayBuilderSummaryPage.
 *
 * @param {Object} options
 * @param {ArrayBuilderOptions} options.arrayBuilderProps array builder options bag
 *   (arrayPath, required, getIntroPath, hasItemsKey, isReviewPage)
 * @param {CustomPageProps} options.customPageProps CustomPage props (data, fullData, goToPath)
 * @param {React.MutableRefObject} options.dataRef ref mirroring the latest form data
 * @param {(data: Object) => void} options.setData ref-aware setData (setDataFromRef)
 */
export function useSummaryPageInit({
  arrayBuilderProps,
  customPageProps,
  dataRef,
  setData,
}) {
  const { arrayPath, required, getIntroPath, hasItemsKey, isReviewPage } =
    arrayBuilderProps;

  useEffect(() => {
    const arrayData = get(arrayPath, customPageProps.data);

    const cleanupEmptyItems = () => {
      // We may end up with empty items if the user navigates back
      // from outside of the array scope, because of FormPage's
      // prePopulateArrayData function which auto populates an
      // array with empty initial values. This will remove any items
      // with no array data.
      if (arrayData?.length) {
        const newArrayData = filterEmptyItems(arrayData);
        if (newArrayData?.length !== arrayData.length) {
          setData({ [arrayPath]: newArrayData });
        }
      }
    };

    const redirectToIntroIfEmpty = () => {
      if (
        !isReviewPage &&
        !arrayData?.length &&
        required(customPageProps.data)
      ) {
        // We shouldn't be on this page if there are no items and its required
        // because the required flow goes intro -> item page with no items
        customPageProps.goToPath(
          withUrlPrefix(
            getIntroPath(customPageProps.fullData),
            customPageProps.urlPrefix,
          ),
        );
      }
    };

    const resetYesNo = () => {
      // We shouldn't persist the 'yes' answer after an item is entered/cancelled
      // We should ask the yes/no question again after an item is entered/cancelled
      // Since it is required, it shouldn't be left null/undefined
      if (!isReviewPage && dataRef.current?.[hasItemsKey]) {
        setData({ [hasItemsKey]: undefined });
      }
    };

    cleanupEmptyItems();
    redirectToIntroIfEmpty();
    resetYesNo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
